import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, ShoppingBag, X } from 'lucide-react';
import { useAuth, apiClient, publicApi, formatApiError } from '../context/AuthContext';
import { useTurnstile } from '../components/Turnstile';
import toast from 'react-hot-toast';
import { cartStorage } from '../utils/cartStorage';

export default function Checkout() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro } = useAuth();
  const { t } = useLanguage();
  const { getToken: getTurnstileToken } = useTurnstile();
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingBank, setSubmittingBank] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    postalCode: '',
    city: '',
    country: (() => { try { return localStorage.getItem('joey_shipping_country') || 'France'; } catch (e) { return 'France'; } })(),
    promoCode: '',
    // Billing address
    useDifferentBilling: false,
    billingFirstName: '',
    billingLastName: '',
    billingCompany: '',
    billingAddress: '',
    billingPostalCode: '',
    billingCity: '',
    billingCountry: 'France',
  });

  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");

  // Pré-remplir le formulaire avec les données du profil utilisateur
  useEffect(() => {
    if (user) {
      const nameParts = (user.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        email: user.email || '',
        phone: user.phone || '',
        address: user.shipping_address || '',
        postalCode: user.shipping_postal_code || '',
        city: user.shipping_city || '',
        country: user.shipping_country || (() => { try { return localStorage.getItem('joey_shipping_country') || 'France'; } catch (e) { return 'France'; } })(),
      }));
    }
  }, [user]);

  const fetchCartAndProducts = useCallback(async () => {
    try {
      if (isAuthenticated) {
        // Utilisateur connecté : charger le panier depuis l'API
        const [cartRes, productsRes, shippingRes] = await Promise.all([
          apiClient.get('/cart'),
          publicApi.get('/products?admin=true'),
          apiClient.get('/shipping-methods')
        ]);
        setCart(cartRes.data);
        setProducts(productsRes.data);
        setShippingMethods(shippingRes.data);
        if (shippingRes.data.length > 0) {
          setSelectedShipping(shippingRes.data[0].id);
        }
      } else {
        // Visiteur non connecté : charger depuis localStorage
        const localCart = cartStorage.getItems();
        setCart(localCart);
        
        const [productsRes, shippingRes] = await Promise.all([
          publicApi.get('/products?admin=true'),
          publicApi.get('/shipping-methods') // Endpoint public pour les invités
        ]);
        setProducts(productsRes.data);
        setShippingMethods(shippingRes.data);
        if (shippingRes.data.length > 0) {
          setSelectedShipping(shippingRes.data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCartAndProducts();
  }, [fetchCartAndProducts]);

  // Charger le code promo depuis localStorage au montage
  useEffect(() => {
    const savedPromo = localStorage.getItem('appliedPromo');
    if (savedPromo) {
      try {
        const promoData = JSON.parse(savedPromo);
        setPromoApplied(promoData);
        setFormData(prev => ({ ...prev, promoCode: promoData.code }));
      } catch (err) {
        localStorage.removeItem('appliedPromo');
      }
    }
  }, []);

  const applyPromoCode = async () => {
    if (!formData.promoCode.trim()) {
      toast.error('Veuillez entrer un code promo');
      return;
    }

    const { subtotalTTC } = calculateTotals();
    
    try {
      const { data } = await apiClient.post(`/promo-codes/validate?code=${formData.promoCode.trim().toUpperCase()}&subtotal=${subtotalTTC}`);
      const promoData = {
        code: data.code,
        discount: data.discount,
        discount_type: data.discount_type,
        discount_amount: data.discount_amount
      };
      setPromoApplied(promoData);
      // Sauvegarder dans localStorage
      localStorage.setItem('appliedPromo', JSON.stringify(promoData));
      setPromoError("");
      toast.success(`Code ${data.code} appliqué !`);
    } catch (err) {
      setPromoApplied(null);
      localStorage.removeItem('appliedPromo');
      setPromoError(formatApiError(err));
      toast.error(formatApiError(err));
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setFormData({ ...formData, promoCode: '' });
    setPromoError("");
    localStorage.removeItem('appliedPromo');
    toast.success('Code promo retiré');
  };

  // For PRO users: item.price is HT. For PUBLIC: item.price is TTC.
  const getCartItemsWithDetails = () => {
    return cart.map(item => {
      const product = products.find(p => p.id === item.product_id);
      let price = item.price;
      if (!price && product) {
        const basePrice = (isPro && product.pro_price) ? product.pro_price : product.price;
        const lensModifier = item.lens_price_modifier || 0;
        price = basePrice + lensModifier;
      }
      return {
        ...item,
        product,
        price,
        total: price * item.quantity
      };
    });
  };

  const calculateTotals = () => {
    const items = getCartItemsWithDetails();
    // Subtotal brut : HT pour pro, TTC pour public
    const subtotalRaw = items.reduce((sum, item) => sum + item.total, 0);
    // Conversion pour cohérence seuils de livraison / promos (basés TTC côté admin)
    const subtotalTTCBase = isPro ? subtotalRaw * 1.20 : subtotalRaw;
    
    // Recalculer le montant de réduction
    let discountAmount = 0;
    if (promoApplied) {
      if (promoApplied.discount_type === 'fixed') {
        discountAmount = promoApplied.discount;
      } else if (promoApplied.discount_type === 'percentage') {
        discountAmount = (subtotalTTCBase * promoApplied.discount) / 100;
      }
    }
    
    const selectedMethod = shippingMethods.find(m => m.id === selectedShipping);
    const totalQty = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
    // Livraison gratuite si seuil free_above (montant) OU free_above_qty (nb articles) atteint
    let shipping = selectedMethod ? (selectedMethod.price_ttc || 0) : 0;
    if (selectedMethod) {
      if (selectedMethod.free_above != null &&
          Number(selectedMethod.free_above) > 0 &&
          subtotalTTCBase >= Number(selectedMethod.free_above)) {
        shipping = 0;
      } else if (selectedMethod.free_above_qty != null &&
                 Number(selectedMethod.free_above_qty) > 0 &&
                 totalQty >= Number(selectedMethod.free_above_qty)) {
        shipping = 0;
      }
    }
    
    // Pro: Sous-total HT -> +TVA -> +livraison TTC -> Total TTC
    // Public: Sous-total TTC (TVA incluse) + livraison TTC -> Total TTC
    const subtotalHT = isPro ? subtotalRaw : +(subtotalRaw / 1.20).toFixed(2);
    const subtotalTTC = isPro ? +(subtotalRaw * 1.20).toFixed(2) : subtotalRaw;
    const tva = +(subtotalTTC - subtotalHT).toFixed(2);
    const subtotalAfterDiscount = subtotalTTC - discountAmount;
    const totalTTC = +(subtotalAfterDiscount + shipping).toFixed(2);

    return {
      subtotalRaw,
      subtotalHT,
      subtotalTTC,
      discountAmount,
      subtotalAfterDiscount,
      tva,
      shipping,
      totalTTC
    };
  };

  const validateForm = () => {
    if (formData.country === '__other__' || !formData.country) {
      toast.error("Ce pays n'est pas pris en charge. Merci de nous contacter à contact@joeycyan.com.");
      return false;
    }
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return false;
    }
    if (!formData.address || !formData.postalCode || !formData.city) {
      toast.error('Veuillez renseigner votre adresse de livraison');
      return false;
    }
    if (!selectedShipping) {
      toast.error('Veuillez sélectionner un mode de livraison');
      return false;
    }
    if (cart.length === 0) {
      toast.error('Votre panier est vide');
      return false;
    }
    return true;
  };

  const buildCustomerData = () => {
    const shippingAddr = `${formData.address}, ${formData.postalCode} ${formData.city}, ${formData.country}`;
    let billingAddr = shippingAddr;
    if (formData.useDifferentBilling) {
      billingAddr = `${formData.billingAddress}, ${formData.billingPostalCode} ${formData.billingCity}, ${formData.billingCountry}`;
    }
    return {
      customer_name: `${formData.firstName} ${formData.lastName}`,
      customer_email: formData.email,
      customer_phone: formData.phone || null,
      shipping_address: shippingAddr,
      billing_address: billingAddr
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const customerData = buildCustomerData();
      let response;
      
      if (isAuthenticated) {
        response = await apiClient.post('/payments/checkout/session', {
          origin_url: window.location.origin,
          promo_code: promoApplied ? formData.promoCode : null
        });
      } else {
        const cartItems = getCartItemsWithDetails();
        const ttoken = await getTurnstileToken();
        response = await publicApi.post('/payments/checkout/session/guest', {
          origin_url: window.location.origin,
          cart_items: cartItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            color: item.color || null,
            lens_option: item.lens_option || null
          })),
          promo_code: promoApplied ? formData.promoCode : null,
          customer_email: formData.email,
          customer_data: customerData
        }, {
          headers: ttoken ? { 'X-Turnstile-Token': ttoken } : {}
        });
      }
      
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
      
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  const handleBankTransfer = async () => {
    if (!validateForm()) return;

    setSubmittingBank(true);

    try {
      const customerData = buildCustomerData();
      const cartItems = getCartItemsWithDetails();
      
      const payload = {
        ...customerData,
        shipping_method_id: selectedShipping,
        promo_code: promoApplied ? formData.promoCode : null,
      };

      // For guests, include items
      if (!isAuthenticated) {
        payload.items = cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          color: item.color || null,
          lens_option: item.lens_option || null
        }));
      }

      const api = isAuthenticated ? apiClient : publicApi;
      const ttoken = await getTurnstileToken();
      const response = await api.post('/orders/bank-transfer', payload, {
        headers: ttoken ? { 'X-Turnstile-Token': ttoken } : {}
      });
      
      if (response.data?.success) {
        // Clear local cart for guests
        if (!isAuthenticated) {
          cartStorage.clear();
        }
        // Clear promo
        localStorage.removeItem('appliedPromo');
        
        const orderNumber = response.data.order?.order_number || '';
        toast.success(`Commande ${orderNumber} créée !`);
        navigate(`/order-confirmation/${orderNumber}?method=bank_transfer`);
      }
    } catch (err) {
      console.error('Bank transfer error:', err);
      toast.error(formatApiError(err));
    } finally {
      setSubmittingBank(false);
    }
  };

  // Country-based shipping filtering (mirrors the /panier logic)
  const authorizedCountries = (() => {
    const all = Array.from(new Set(
      shippingMethods.flatMap(m => Array.isArray(m.countries) ? m.countries : [])
        .map(c => String(c).trim())
        .filter(Boolean)
    ));
    const franceKey = all.find(c => c.toLowerCase() === 'france');
    const rest = all.filter(c => c !== franceKey).sort((a, b) => a.localeCompare(b, 'fr'));
    return franceKey ? [franceKey, ...rest] : rest;
  })();
  const isCountryUnsupported = formData.country === '__other__';
  const methodsForCountry = (!formData.country || isCountryUnsupported)
    ? []
    : shippingMethods.filter(m => Array.isArray(m.countries) && m.countries.some(c => c.toLowerCase() === String(formData.country).toLowerCase()));

  // Persist selected country + reset shipping method when country changes
  useEffect(() => {
    try { localStorage.setItem('joey_shipping_country', formData.country || ''); } catch (e) {}
    if (isCountryUnsupported || !formData.country) {
      if (selectedShipping) setSelectedShipping(null);
      return;
    }
    if (methodsForCountry.length === 0) {
      if (selectedShipping) setSelectedShipping(null);
    } else if (!methodsForCountry.some(m => m.id === selectedShipping)) {
      setSelectedShipping(methodsForCountry[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country, shippingMethods]);

  const totals = calculateTotals();
  const cartItems = getCartItemsWithDetails();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#00aaff] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <ShoppingBag size={64} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Panier vide</h2>
          <p className="text-gray-500 mb-6">Ajoutez des produits pour finaliser votre commande</p>
          <Link to="/collection" className="inline-block bg-[#00aaff] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#0099ee] transition-colors">
            Voir la collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Link to="/panier" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00aaff] mb-6 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm">Retour au panier</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">Finaliser la commande</h1>
          <p className="text-gray-500">Vos informations de livraison et de contact</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Section */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#00aaff]/10 flex items-center justify-center">
                    <span className="text-[#00aaff] font-bold">✉️</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Contact</h2>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.phone')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  />
                </div>
              </div>

              {/* Shipping Address Section */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#00aaff]/10 flex items-center justify-center">
                    <span className="text-[#00aaff] font-bold">📍</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t("checkout.shippingAddress")}</h2>
                </div>

                {/* Nom et Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.firstName')} *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      placeholder="Jean"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.lastName')} *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      placeholder="Dupont"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                </div>

                {/* Société */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Société (optionnel)</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Mon Entreprise SARL"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.address')} *</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    required
                    placeholder="15 rue de la Paix"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.postalCode')} *</label>
                    <input
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                      required
                      placeholder="75001"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.city')} *</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                      placeholder="Paris"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.country')} *</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    required
                    data-testid="checkout-country-select"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                  >
                    {authorizedCountries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="__other__">Autre</option>
                  </select>
                </div>
              </div>

              {/* Billing Address Toggle */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.useDifferentBilling}
                      onChange={(e) => setFormData({ ...formData, useDifferentBilling: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00aaff]"></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-[#00aaff] transition-colors">
                    Utiliser une adresse de facturation différente
                  </span>
                </label>

                {/* Billing Address Fields */}
                {formData.useDifferentBilling && (
                  <div className="mt-6 space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Adresse de facturation</h3>
                    
                    {/* Billing Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.firstName')} *</label>
                        <input
                          type="text"
                          value={formData.billingFirstName}
                          onChange={(e) => setFormData({...formData, billingFirstName: e.target.value})}
                          required={formData.useDifferentBilling}
                          placeholder="Jean"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.lastName')} *</label>
                        <input
                          type="text"
                          value={formData.billingLastName}
                          onChange={(e) => setFormData({...formData, billingLastName: e.target.value})}
                          required={formData.useDifferentBilling}
                          placeholder="Dupont"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>
                    </div>

                    {/* Billing Company */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Société (optionnel)</label>
                      <input
                        type="text"
                        value={formData.billingCompany}
                        onChange={(e) => setFormData({...formData, billingCompany: e.target.value})}
                        placeholder="Mon Entreprise SARL"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                      />
                    </div>

                    {/* Billing Address */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.address')} *</label>
                      <input
                        type="text"
                        value={formData.billingAddress}
                        onChange={(e) => setFormData({...formData, billingAddress: e.target.value})}
                        required={formData.useDifferentBilling}
                        placeholder="15 rue de la Paix"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                      />
                    </div>

                    {/* Billing Postal & City */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.postalCode')} *</label>
                        <input
                          type="text"
                          value={formData.billingPostalCode}
                          onChange={(e) => setFormData({...formData, billingPostalCode: e.target.value})}
                          required={formData.useDifferentBilling}
                          placeholder="75001"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.city')} *</label>
                        <input
                          type="text"
                          value={formData.billingCity}
                          onChange={(e) => setFormData({...formData, billingCity: e.target.value})}
                          required={formData.useDifferentBilling}
                          placeholder="Paris"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>
                    </div>

                    {/* Billing Country */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">{t('checkout.country')} *</label>
                      <select
                        value={formData.billingCountry}
                        onChange={(e) => setFormData({...formData, billingCountry: e.target.value})}
                        required={formData.useDifferentBilling}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                      >
                        <option value="France">France</option>
                        <option value="Belgique">Belgique</option>
                        <option value="Suisse">Suisse</option>
                        <option value="Luxembourg">Luxembourg</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Shipping Method */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900 mb-4">{t('checkout.shippingMethod')}</h2>
                {isCountryUnsupported ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="checkout-country-unsupported">
                    Ce pays n'est pas pris en charge pour le moment, merci de nous contacter pour en savoir plus sur <a href="mailto:contact@joeycyan.com" className="font-semibold underline hover:text-amber-700">contact@joeycyan.com</a>
                  </div>
                ) : methodsForCountry.length === 0 ? (
                  <p className="text-gray-500 text-sm">Aucun mode de livraison disponible pour {formData.country}.</p>
                ) : (
                  <div className="space-y-3">
                    {methodsForCountry.map(method => {
                      const subtotalTTC = cartItems.reduce((sum, i) => {
                        const p = i.price !== undefined ? Number(i.price) : (i.product?.price || 0);
                        return sum + p * (i.quantity || 1);
                      }, 0);
                      const totalQty = cartItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
                      const isFreeByAmount = method.free_above != null && Number(method.free_above) > 0 && Number(method.price_ttc) > 0 && subtotalTTC >= Number(method.free_above);
                      const isFreeByQty = method.free_above_qty != null && Number(method.free_above_qty) > 0 && Number(method.price_ttc) > 0 && totalQty >= Number(method.free_above_qty);
                      const missingForFree = method.free_above != null && Number(method.free_above) > 0 && Number(method.price_ttc) > 0 && subtotalTTC < Number(method.free_above)
                        ? Number(method.free_above) - subtotalTTC
                        : 0;
                      const missingQtyForFree = method.free_above_qty != null && Number(method.free_above_qty) > 0 && Number(method.price_ttc) > 0 && totalQty < Number(method.free_above_qty)
                        ? Number(method.free_above_qty) - totalQty
                        : 0;
                      const effectivePrice = (isFreeByAmount || isFreeByQty) ? 0 : (Number(method.price_ttc) || 0);
                      return (
                        <label
                          key={method.id}
                          className={`block border-2 rounded-xl p-4 cursor-pointer transition-all ${
                            selectedShipping === method.id
                              ? 'border-[#00aaff] bg-[#00aaff]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="shipping"
                                value={method.id}
                                checked={selectedShipping === method.id}
                                onChange={(e) => setSelectedShipping(e.target.value)}
                                className="w-4 h-4 text-[#00aaff]"
                              />
                              <div>
                                <p className="font-semibold text-gray-900">{method.name}</p>
                                {method.description && (
                                  <p className="text-sm text-gray-500 mt-0.5">{method.description}</p>
                                )}
                                {isFreeByAmount && (
                                  <p className="text-xs text-green-600 font-semibold mt-0.5">Offerte — commande ≥ {Number(method.free_above).toFixed(2)}€</p>
                                )}
                                {isFreeByQty && !isFreeByAmount && (
                                  <p className="text-xs text-green-600 font-semibold mt-0.5">Offerte — ≥ {method.free_above_qty} article{method.free_above_qty > 1 ? 's' : ''}</p>
                                )}
                                {!isFreeByAmount && !isFreeByQty && missingForFree > 0 && (
                                  <p className="text-xs text-gray-500 mt-0.5">Plus que <span className="font-semibold text-[#00aaff]">{missingForFree.toFixed(2)}€</span> pour la livraison offerte</p>
                                )}
                                {!isFreeByAmount && !isFreeByQty && missingQtyForFree > 0 && (
                                  <p className="text-xs text-gray-500 mt-0.5">Ou encore <span className="font-semibold text-[#00aaff]">{missingQtyForFree} article{missingQtyForFree > 1 ? 's' : ''}</span> pour la livraison offerte</p>
                                )}
                              </div>
                            </div>
                            <div className={`font-bold ${effectivePrice === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                              {effectivePrice === 0 ? 'Gratuit' : `${effectivePrice.toFixed(2)}€`}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Récapitulatif</h2>

                {/* Cart Items */}
                <div className="space-y-4 mb-6">
                  {cartItems.map((item, index) => {
                    // Image à afficher : priorité au snapshot stocké au moment de l'ajout,
                    // puis image de la couleur sélectionnée, puis image de base du produit.
                    let displayImage = item.product_image;
                    if (!displayImage && item.color && item.product?.color_images?.[item.color]?.length > 0) {
                      displayImage = item.product.color_images[item.color][0];
                    }
                    if (!displayImage && item.product?.images?.[0]) {
                      displayImage = item.product.images[0];
                    }
                    return (
                    <div key={index} className="flex gap-3">
                      {displayImage && (
                        <img 
                          src={displayImage} 
                          alt={item.product?.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900">{item.product?.name}</p>
                        {item.color && (
                          <p className="text-xs text-gray-500">
                            Couleur : {typeof item.color === 'string' ? item.color : item.color?.name || item.color}
                          </p>
                        )}
                        {item.lens_option && (
                          <p className="text-xs text-gray-500">
                            Verres : {item.lens_option}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Qté : {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">
                          {item.total.toFixed(2)}€
                          {isPro && <span className="text-[10px] text-amber-700 font-semibold ml-1">HT</span>}
                        </p>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Promo Code */}
                <div className="mb-6">
                  {promoApplied ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-green-700">{promoApplied.code}</span>
                        <span className="text-xs text-green-600">
                          {promoApplied.discount_type === 'fixed' ? `-${promoApplied.discount}€` : `-${promoApplied.discount}%`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={removePromo}
                        className="text-gray-400 hover:text-red-400"
                        title="Retirer le code"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.promoCode}
                        onChange={(e) => setFormData({...formData, promoCode: e.target.value.toUpperCase()})}
                        onKeyDown={(e) => e.key === 'Enter' && applyPromoCode()}
                        placeholder="Code promo"
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00aaff]"
                      />
                      <button
                        type="button"
                        onClick={applyPromoCode}
                        className="bg-[#00aaff] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#0099ee] transition-colors"
                      >
                        OK
                      </button>
                    </div>
                  )}
                  {promoError && <p className="text-xs text-red-500 mt-2">{promoError}</p>}
                </div>

                {/* Totals */}
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{isPro ? 'Sous-total HT' : 'Sous-total TTC'}</span>
                    <span className="font-semibold text-gray-900">
                      {totals.subtotalHT.toFixed(2)}€{isPro && <span className="text-xs text-amber-700 ml-1">HT</span>}
                    </span>
                  </div>
                  {isPro && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">TVA (20%)</span>
                      <span className="font-semibold text-gray-900">{totals.tva.toFixed(2)}€</span>
                    </div>
                  )}
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Réduction</span>
                      <span className="font-semibold text-green-600">-{totals.discountAmount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Livraison TTC</span>
                    <span className={`font-semibold ${totals.shipping === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                      {totals.shipping === 0 ? 'Gratuite' : `${totals.shipping.toFixed(2)}€`}
                    </span>
                  </div>
                  {!isPro && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>dont TVA (20%)</span>
                      <span>{totals.tva.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="border-t-2 border-gray-200 pt-3 flex justify-between">
                    <span className="font-bold text-gray-900">Total TTC</span>
                    <span className="font-black text-xl text-[#00aaff]">{totals.totalTTC.toFixed(2)}€</span>
                  </div>
                </div>

                {/* Payment Buttons */}
                <button
                  type="submit"
                  disabled={submitting || submittingBank}
                  data-testid="pay-card-btn"
                  className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold mt-6 hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lock size={18} />
                  {submitting ? 'Redirection...' : 'Payer par carte bancaire'}
                </button>

                <button
                  type="button"
                  onClick={handleBankTransfer}
                  disabled={submitting || submittingBank}
                  data-testid="pay-bank-transfer-btn"
                  className="w-full bg-[#00aaff] text-white py-4 rounded-xl font-bold mt-3 hover:bg-[#0099ee] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
                  {submittingBank ? 'Création...' : 'Payer par virement bancaire'}
                </button>

                <p className="text-xs text-center text-gray-400 mt-3 flex items-center justify-center gap-1">
                  <Lock size={12} className="text-green-600" />
                  Paiement 100% sécurisé SSL
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
